import {yupResolver} from '@hookform/resolvers/yup'
import {FullscreenOverlayWithSpinner} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {type ReactElement, useCallback, useEffect, useMemo, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {LayoutSkeleton} from './components/LayoutSkeleton'
import {ActionProvider} from './contexts/ActionContext'
import {ConfigProvider} from './contexts/ConfigContext'
import {StatusProvider} from './contexts/StatusContext'
import * as classes from './Form.classes'
import {getFormValidationSchema} from './lib/validation'
import type {OrderFormProps, OrderFormState} from './types'
import {renderWidget} from './widgets'

export const OrderForm = ({
  defaultValues,
  layout,
  isLoading,
  isFetching,
  isOverlayVisible,
  onChange,
  config,
  onSubmit,
  outerDefaults,
}: OrderFormProps): ReactElement => {
  const i18n = useI18n()
  const [nddOffer, setNddOffer] = useState<string | undefined>(undefined)
  const resolver = useMemo(
    () =>
      yupResolver(
        getFormValidationSchema(outerDefaults?.taxi_class ?? '', i18n, config),
        {},
        {
          mode: 'async',
        },
      ),
    [outerDefaults?.taxi_class, config, i18n],
  )

  const form = useForm<OrderFormState>({
    defaultValues,
    resolver,
    mode: 'onBlur',
  })

  const handleChange = useCallback(
    // скидываем поле ndd-оффера на изменение стейта формы
    (data: OrderFormState): void => {
      const offer = data.ndd_offer_name
      if (offer) {
        if (offer === nddOffer || data.vertical_name !== 'ndd_self_pickup') {
          setNddOffer(undefined)
          form.setValue('ndd_offer_name', undefined)
          data.ndd_offer_name = undefined
        } else {
          setNddOffer(offer)
        }
      }
      onChange?.(data)
    },
    [form, nddOffer, onChange],
  )

  useEffect(() => {
    if (onChange) {
      return form.watch(handleChange).unsubscribe
    }
  }, [form, handleChange, onChange])

  if (!isLoading && !layout) {
    throw new Error('Root form widget is not found')
  }

  /** ресет формы для обновления стейта при смене внешних дефолтов
   * более адекватного способа внешнего управления стейтом в RHF я не нашла
   */
  useEffect(() => {
    if (outerDefaults) {
      form.reset(formValues => ({...formValues, ...outerDefaults}))
    }
  }, [form, onChange, outerDefaults])

  return (
    <StatusProvider isFetching={isFetching}>
      <ActionProvider onSubmit={onSubmit}>
        <ConfigProvider {...config}>
          <FormProvider {...form}>
            {isOverlayVisible && <FullscreenOverlayWithSpinner />}
            <main className={classes.main}>{layout ? renderWidget(layout, 'layout') : <LayoutSkeleton />}</main>
          </FormProvider>
        </ConfigProvider>
      </ActionProvider>
    </StatusProvider>
  )
}
